<template>
  <div
    class="pin-page h-100 text-center"
    :style="pageStyle"
  >
    <b-container
      fluid
      class="h-100"
    >
      <b-row class="h-100 align-content-center">
        <b-col cols="12">
          <b-navbar
            variant="faded"
            type="light"
          >
            <b-navbar-brand href="#">
              <img
                class="logo"
                :class="{ whitelabel: isWhitelabelEnabled && whitelabelLogo }"
                :src="logoSource"
                alt=""
              >
            </b-navbar-brand>
          </b-navbar>
          <PinBlockComponent
            :title="isTimeClock ? 'Enter Pin to Time Clock' : $t('pinscreen.enter-pin-to-login')"
            :wrong-pin-msg="$t('pinscreen.wrong-pin')"
            :is-display-default-pin="isDisplayDefaultPin"
            :default-pin="DEFAULT_PIN"
            :mode="isTimeClock ? 'timeclock' : 'login'"
            @get-employee="getEmployeeByPin"
            @get-timeclock="handleTimeClockVerification"
          />
          
          <!-- Action buttons for mobile - shown under pin -->
          <div class="action-buttons d-md-none">
            <button
              @click="goToTimeClock"
              :class="{ 'time-clock-active': isTimeClock }"
            >
              <svg class="icon icon-icon-clock">
                <use 
                  xlink:href="#icon-icon-clock" 
                  :class="{ 'clock-active': isTimeClock, 'clock-inactive': !isTimeClock }"
                />
              </svg>
              <span>{{ $t("pinscreen.time-clock") }}</span>
            </button>
            <button
              @click="goToSync"
              :class="{'disabled':isSyncInProgress || !connection}"
              :disabled="isSyncInProgress || !connection"
            >
              <svg class="icon icon-icon-popup-sync">
                <use xlink:href="#icon-icon-popup-sync" />
              </svg>
              <span>{{ $t("syncdatapopup.sync-data") }}</span>
            </button>
          </div>
        </b-col>

        <div
          v-if="terminal"
          class="terminal-version"
        >
          {{ terminal.name }}: {{ terminal.id }}
        </div>
        
        <!-- Action buttons for desktop - fixed on right side -->
        <div class="action-buttons d-none d-md-flex">
          <button
            @click="goToTimeClock"
            :class="{ 'time-clock-active': isTimeClock }"
          >
            <svg class="icon icon-icon-clock">
              <use 
                xlink:href="#icon-icon-clock" 
                :class="{ 'clock-active': isTimeClock, 'clock-inactive': !isTimeClock }"
              />
            </svg>
            <span>{{ $t("pinscreen.time-clock") }}</span>
          </button>
          <button
            @click="goToSync"
            :class="{'disabled':isSyncInProgress || !connection}"
            :disabled="isSyncInProgress || !connection"
          >
            <svg class="icon icon-icon-popup-sync">
              <use xlink:href="#icon-icon-popup-sync" />
            </svg>
            <span>{{ $t("syncdatapopup.sync-data") }}</span>
          </button>
        </div>
        <div class="pin-version">
          {{ VERSION }}
        </div>
      </b-row>

      <b-modal
        id="modal-1"
        v-model="modalShow"
        :body-bg-variant="bodyBgVariant"
        no-close-on-backdrop
        hide-footer
        title="Please, enter terminal verification code:"
      >
        <div class="pin-num text-center">
          <ul class="pin-num-list">
            <li><span>{{ getCodeSymbol(0) }}</span></li>
            <li><span>{{ getCodeSymbol(1) }}</span></li>
            <li><span>{{ getCodeSymbol(2) }}</span></li>
            <li><span>{{ getCodeSymbol(3) }}</span></li>
            <li><span>{{ getCodeSymbol(4) }}</span></li>
            <li><span>{{ getCodeSymbol(5) }}</span></li>
          </ul>
        </div>

        <div class="pin-keyboard text-center">
          <ul class="pin-keyboard-list">
            <li>
              <span
                class="pin-key"
                @click="typeCode(1)"
              >1</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(2)"
              >2</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(3)"
              >3</span>
            </li>

            <li>
              <span
                class="pin-key"
                @click="typeCode(4)"
              >4</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(5)"
              >5</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(6)"
              >6</span>
            </li>

            <li>
              <span
                class="pin-key"
                @click="typeCode(7)"
              >7</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(8)"
              >8</span>
            </li>
            <li>
              <span
                class="pin-key"
                @click="typeCode(9)"
              >9</span>
            </li>

            <li><span class="pin-key pin-key_call">C</span></li>

            <li>
              <span
                class="pin-key"
                @click="typePin(0)"
              >0</span>
            </li>
          </ul>
        </div>

        <b-button
          class="mt-4"
          variant="primary"
          block
        >
          {{ $t('verificationcodeinputpopup.accept') }}
        </b-button>
      </b-modal>

      <b-modal
        id="success-modal"
        centered
        hide-footer
        hide-header
        body-class="text-center p-4"
      >
        <div class="d-flex flex-column align-items-center">
          <div class="mb-3 success-border">
            <svg class="icon icon-icon-clock success-icon">
              <use xlink:href="#icon-icon-clock" />
            </svg>          
          </div>
          <h5 class="mb-3">
            {{ employee.name }}
          </h5>
          <div
            class="mb-4 d-flex justify-content-between"
            v-if="clockEntryResult"
          >
            <div>
              <span :class="{ 'clock-in': clockEntryResult.is_clock_in, 'clock-out': !clockEntryResult.is_clock_in }">
                {{ clockEntryResult.is_clock_in ? 'CLOCK IN' : 'CLOCK OUT' }}
              </span>
            </div>
            <div class="pl-3">
              <h5 class="mb-0 font-weight-normal time-color">
                {{ clockEntryResult.time }}
              </h5>
            </div>
          </div>
          <b-button 
            variant="primary"
            class="w-50"
            @click="$bvModal.hide('success-modal')"
          >
            OK!
          </b-button>
        </div>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import store from '../store/store';
import i18n from '@/plugins/i18n';
import PinBlockComponent from './particle/PinBlockComponent';
import { mapGetters, mapActions } from "vuex";
import dashboardService from "../services/dashboard-service";
import moment from 'moment';
import {
  APP_VERSION,
  APP_VERSION_LABEL,
  fetchLatestAppVersion,
  normalizeAppVersion
} from "@/helpers/appVersion";

const DEFAULT_PIN = 1234;
export default {
  name: 'AppPin',
  components: {
    PinBlockComponent
  },
  data() {
    return {
      pin: '',
      code: '',
      VERSION: '',
      modalShow: false,
      bodyBgVariant: 'light',
      employee: 'en', // default
      pinMsg: this.$i18n.t('pinscreen.enter-pin-to-login'),
      // terminal: null,
      isDisplayDefaultPin: false,
      DEFAULT_PIN,
      isTimeClock: false,
      clockEntryResult: null,
      hasPromptedForVersionRefresh: false
    };
  },
  computed: {
    ...mapGetters(['isSyncInProgress']),
    pinLength() {
      return this.pin.length;
    },
    isShiftOpen() {
      return this.$store.getters.isShiftOpen;
    },
    terminal() {
      return this.$store.getters['config/terminal'];
    },
    connection () {
      return this.$store.getters.connection;
    },
    isWhitelabelEnabled() {
      return this.$store.getters['config/isWhitelabelEnabled'];
    },
    primaryColor() {
      return this.$store.getters['config/primaryColor'];
    },
    whitelabelLogo() {
      return this.$store.getters['config/whitelabelLogo'];
    },
    logoSource() {
      return this.isWhitelabelEnabled && this.whitelabelLogo ? this.whitelabelLogo : require("../assets/images/logo.svg");
    },
    pageStyle() {
      return this.isWhitelabelEnabled ? { background: `linear-gradient(180deg, ${this.primaryColor} 0%, ${this.primaryColor} 100%)` } : {};
    }
  },
  watch: {
    connection(isOnline) {
      if (isOnline) {
        this.checkForVersionRefresh();
      }
    }
  },
  beforeMount: async function () {
    this.VERSION = APP_VERSION_LABEL;
  },
  created: async function () {
    await this.fetchEmployees()
    const defaultEmployee = this.$store.getters['config/employeeById'](1);
    if (defaultEmployee && defaultEmployee.pin === DEFAULT_PIN)
      this.isDisplayDefaultPin = true;
  },
  async mounted() {
    this.pin = '';
    await this.checkForVersionRefresh();
  },
  methods: {
    ...mapActions({
      fetchEmployees: 'config/fetchEmployeesFromDb'
    }),
    goToTimeClock() {
      this.isTimeClock = !this.isTimeClock;
    },
    async handleTimeClockVerification(employee) {
      if (employee) {
        this.employee = employee;
        try {
          this.clockEntryResult = await dashboardService.addTimeClockEntry(employee.id);
          this.clockEntryResult.time = moment.utc(this.clockEntryResult.time).local().format('hh:mm A');
          this.isTimeClock = false;
          this.$bvModal.show('success-modal');
        } catch (error) {
          console.error('Error adding time clock entry:', error);
        }
      }
    },
    goToSync() {
      this.$router.push({ name: 'Synchronize' });
    },
    async checkForVersionRefresh() {
      if (!navigator.onLine || !this.connection) {
        return;
      }

      try {
        const remoteVersion = await fetchLatestAppVersion();
        const currentVersion = normalizeAppVersion(APP_VERSION);

        if (!remoteVersion || remoteVersion === currentVersion || this.hasPromptedForVersionRefresh) {
          return;
        }

        this.hasPromptedForVersionRefresh = true;
        this.$bvModal.show('sw-update-modal');
      } catch (error) {
        console.warn('[VersionCheck] Unable to check latest app version:', error);
      }
    },
    async getEmployeeByPin(employee) {
      if (!employee) {
        console.warn('Could not find user with given pin...');
      } else {
        await this.$store.dispatch('setEmployee', employee)
        store.commit('unlock');
        this.clearPin();
        this.changeLocale(employee.language);
        await store.dispatch('setEmployee', { employee });
        if (!this.isShiftOpen) return await this.$router.push({ name: 'Shift' });
        this.terminal.mode === 1 ? await this.$router.push({ name: 'Tables' }) : await this.$router.push({
          name: 'Order',
          params: {
            tableID: null,
            id: 'new'
          }
        });
      }
    },
    onKey(event) {
      if (event.keyCode >= 48 && event.keyCode <= 57) {
        this.typePin(event.keyCode - 48);
      } else if (event.keyCode === 8) {
        this.clearPin();
      }
    },
    getCodeSymbol(position) {
      if (position >= this.code.length) {
        return '';
      } else {
        return this.code[position];
      }
    },
    clearPin() {
      this.pin = '';
    },
    clearCode() {
      this.code = '';
    },
    changeLocale(locale) {
      i18n.locale = locale;
    }
  }
};
</script>

<style lang="scss" scoped>
.pin-page {
  background: linear-gradient(180deg, #1976D2 0%, #2196F3 100%);
  color: #fff;
  min-height: 100vh;

  h6 {
    font-size: 15px;
    line-height: 18px;
  }

  .pin-block {
    padding-top: 15px;
  }

  @media (max-height: 376px) {
    height: auto !important;
  }
}

.navbar {
  padding: 30px 0 30px;
}

.navbar-brand {
  padding: 0;
  margin: 0 auto;
  height: 60px;
}

.terminal-version {
  position: absolute;
  bottom: 20px;
  font-size: .875rem;
  line-height: 1rem;
  color: #fff;
  pointer-events: none;
}

.pin-version {
  right: 20px;
}

.terminal-version {
  left: 20px
}

// Desktop version - fixed on right side, stacked vertically, aligned to bottom
.action-buttons.d-none.d-md-flex {
  position: fixed;
  right: 20px;
  bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 10;

  button {
    background: #FFFFFF;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    border: none;
    padding: 15px 25px 8px;
    border: none !important;
    outline: none !important;
    min-width: 120px;
    transition: all 0.2s ease;
    
    &:hover:not(.disabled) {
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
      transform: translateY(-1px);
    }
    
    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    span {
      display: block;
      padding: 5px 0;
      font-size: 14px;
    }

    .icon-icon-popup-sync,
    .icon-icon-clock {
      fill: #2196F3;
    }
  }
}

// Mobile version - stack buttons under pin icon, full width
.action-buttons.d-md-none {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  margin: 30px auto 0;
  padding: 0;

  button {
    background: #FFFFFF;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    border: none;
    padding: 15px 20px 8px;
    width: 100%;
    border: none !important;
    outline: none !important;
    
    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    span {
      display: block;
      padding: 5px 0;
      font-size: 14px;
    }

    .icon-icon-popup-sync,
    .icon-icon-clock {
      fill: #2196F3;
    }
  }
}
.disabled{
  opacity:0.7;
  cursor:default;
}

.time-clock-active {
  background-color: #0D47A1 !important;
  color: white !important;
}

.success-border {
  border-radius: 20px;
  background-color: #2196F3;
  width: 50px;
  height: 50px;
  position: relative;
}

.success-icon {
  color: #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.clock-in{
  background-color: #8BC34A;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
}

.clock-out{
  background-color: #F44336;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
}

.time-color {
  color: #616161;
}

.clock-active{
  color: #fff;
}
.clock-inactive{
  color: #2196F3;
}

</style>
